function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { clsx } from 'clsx';
import produce from 'immer';
import camelCase from 'lodash/camelCase';
import { EntrySectionUpdates, FiltersOperator, MetricEntryBehavior, QueryFilterEntry, RowLabelType, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import { Event, EventItem } from 'common/query-builder/query-block/events-menu/types';
import { MetricType } from 'common/types/reports/bookmark';
import { QueryBlockReact } from 'common/query-builder/query-block';
import { MenuType } from 'common/query-builder/query-block/types';
import { ButtonTheme } from 'common/components/button/constants';
import { SelectorType as QueryBlockSelectorType } from 'common/query-builder/query-block/constants';
import { OverflowControls } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/controls';
import { ControlBarAction, ControlBarActionItem, ControlBarActionItems } from 'common/query-builder/query-entry-section/constants';
import { ButtonReact } from 'common/components/button';
import { createFirstTimeEventFilter, isFirstTimeEventFilter } from 'common/widgets/property-filter-menu/models/utils';
// eslint-disable-next-line import/no-cycle
import { QueryEntrySectionReact } from 'common/query-builder/query-entry-section';
import { getFilterEntriesFromFilters } from 'iron/common/query-builder/query-entry-section/row-types/filter/utils';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { RowFiltersPrefix } from 'common/query-builder/query-entry-section/components/filters';
import { isXauMathType } from 'common/query-builder/query-builder/util';
import { BEHAVIOR_PROPERTIES } from 'common/query-builder/query-block/properties-menu/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { prompt } from 'common/components/confirm';
import type { InheritedEntryRowProps, RowProps } from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import { SVG_ICONS } from 'common/components/svg-icon/icons';
import { PropertySectionItem } from 'common/query-builder/query-block/properties-menu/item-menu-types';
import type { BookmarkDragMetadata } from 'common/report/insights/contexts/drag-drop/types';
import { getEventBehaviorName } from 'common/report/insights/contexts/utils/metric-names';
import { useTranslation } from 'common/i18n/hooks';
import { BehaviorSelector } from '../behavior-selector';
import styles from './index.module.styl';
export type EventRowProps = RowProps & {
  additionalOverflowControls?: ControlBarActionItem[];
  behavior: MetricEntryBehavior;
  /** renders a label inside the row icon indicator */
  iconLabel?: string | null;
  /** For styling secondary row like "Add Step" / "Add Event" */
  isSecondaryRow?: boolean;
  /** overrides the selected event name */
  label?: string | null;
  onClose?: (() => void) | null | undefined;
  onDelete?: (() => void) | null | undefined;
  onDuplicate?: (() => void) | null | undefined;
  onSelect?: ((behavior: MetricEntryBehavior) => void) | null | undefined;
  /** renders a label before the event name */
  prefixLabel?: string | null;
  /**
   * Open the query block for event selection.
   */
  openQueryBlock?: boolean;
  /**
   * Show this icon for the row icon.
   */
  rowIcon?: keyof typeof SVG_ICONS | null;
  /** renders a label after the event name */
  suffixLabel?: string | null;

  /** hides all overflow controls for a given event */
  hideControls?: boolean;

  /** Enables cohort filters */
  enableCohortFilters?: boolean;

  /** Injects custom filter options */
  otherFilterOptions?: Array<PropertySectionItem<string>>;
};

/**
 * Updates filters for a given behavior along with the filter determiner
 */
function handleFiltersUpdate(props: {
  behavior: MetricEntryBehavior;
  filters?: PropertyFilter[];
  updatedFiltersOperator?: FiltersOperator | null;
  onSelect: EventRowProps[`onSelect`];
}) {
  const result = produce(props.behavior, behavior => {
    var _props$filters;
    behavior.filters = (_props$filters = props.filters) !== null && _props$filters !== void 0 ? _props$filters : props.behavior.filters;
    if (props.updatedFiltersOperator) {
      behavior.filtersDeterminer = props.updatedFiltersOperator === FiltersOperator.And ? "all" : "any";
    }
  });
  props.onSelect(result);
}
export function RowFilters(props: InheritedEntryRowProps & {
  /** specifies which drag and drop group this section is attached to */
  dragData?: Partial<BookmarkDragMetadata>;
  behavior: MetricEntryBehavior;
  dataGroupId?: string | null;
  disableCloneEntry?: boolean;
  disabledItems?: Obj;
  eventNames?: string[];
  hideEvents?: boolean;
  enableCohortFilters?: boolean;
  isStaging?: boolean | Obj | null;
  onAbortStage: () => void;
  onSelect: EventRowProps[`onSelect`];
  otherFilterOptions?: EventRowProps[`otherFilterOptions`];
}) {
  var _props$sectionProps$p, _props$eventNames, _props$hideEvents, _ref2, _props$otherFilterOpt, _props$disabledItems, _props$disableCloneEn;
  const filtersOperator = props.behavior.filtersDeterminer === "all" ? FiltersOperator.And : FiltersOperator.Or;
  // const [firstTimeFilters, regularFilters] = partition(props.behavior.filters, isFirstTimeEventFilter);
  const regularFilters = props.behavior.filters;
  const firstTimeFilters = [];

  /** renders the AND/OR filter operator selector */
  const conjunctionRenderer = _ref => {
    let {
      index: filterIndex,
      entry: filterEntry
    } = _ref;
    return filterIndex === 0 && regularFilters.length > 1 && <RowFiltersPrefix filterIndex={filterIndex} filter={new PropertyFilter((filterEntry as QueryFilterEntry).event)} filtersOperator={props.behavior.filtersDeterminer === "all" ? FiltersOperator.And : FiltersOperator.Or} isReadOnlyMode={false} forNewUI handleChange={item => handleFiltersUpdate({
      behavior: props.behavior,
      updatedFiltersOperator: item.value as FiltersOperator,
      onSelect: props.onSelect
    })} />;
  };
  return <QueryEntrySectionReact dragData={_objectSpread(_objectSpread({}, props.dragData), {}, {
    type: "inline-filter"
  })} containedSection indentFilters elref={camelCase("entry-filters")} autoactivatePlaceholders enableAllDataGroupsForScdProps={props.sectionProps.enableAllDataGroupsForScdProps} excludeDimensionProperties={props.sectionProps.excludeDimensionProperties} includeCustomProperties={props.sectionProps.includeCustomProperties} showBorrowedProperties={props.sectionProps.showBorrowedProperties} hideEditControls={props.sectionProps.hideEditControls} propertyCombineMode={(_props$sectionProps$p = props.sectionProps.propertyCombineMode) !== null && _props$sectionProps$p !== void 0 ? _props$sectionProps$p : undefined} eventNames={(_props$eventNames = props.eventNames) !== null && _props$eventNames !== void 0 ? _props$eventNames : []} chartType={props.currentChartType} supportB2bCohorts={props.sectionProps.dataGroupsEnabled} dataGroupsEnabled={props.sectionProps.dataGroupsEnabled} projectId={props.sectionProps.projectId} workspaceId={props.sectionProps.workspaceId} dataGroupId={props.dataGroupId} disableAddEntry rowTypes={[SelectorTypeDefinitions.Filter]} rowLabelType={RowLabelType.None} hideEvents={(_props$hideEvents = props.hideEvents) !== null && _props$hideEvents !== void 0 ? _props$hideEvents : false} otherFilterOptions={(_ref2 = (_props$otherFilterOpt = props.otherFilterOptions) !== null && _props$otherFilterOpt !== void 0 ? _props$otherFilterOpt : props.sectionProps.otherFilterOptions) !== null && _ref2 !== void 0 ? _ref2 : []} projectHasCohorts={props.enableCohortFilters} projectHasPeople disabledItems={(_props$disabledItems = props.disabledItems) !== null && _props$disabledItems !== void 0 ? _props$disabledItems : {}} rowDraggable managedDragAndDrop trackProperties={{
    sectionName: "inline-filter"
  }} disableCloneEntry={(_props$disableCloneEn = props.disableCloneEntry) !== null && _props$disableCloneEn !== void 0 ? _props$disableCloneEn : false} entries={getFilterEntriesFromFilters([...regularFilters, ...firstTimeFilters], filtersOperator)} sectionName={"inline-filter"} renderSuffix={regularFilters.length > 1 ? conjunctionRenderer : undefined} onUpdate={(updates: EntrySectionUpdates<QueryFilterEntry>) => {
    handleFiltersUpdate({
      behavior: props.behavior,
      filters: updates.entries.map(entry => entry.event as PropertyFilter),
      onSelect: props.onSelect
    });
  }} stagingRow={props.isStaging} onAbortStage={() => props.onAbortStage()} data-sentry-element="QueryEntrySectionReact" data-sentry-component="RowFilters" data-sentry-source-file="index.tsx" />;
}
export type BehaviorRowProps = EventRowProps & {
  metricIndex?: number;
  behaviorIndex?: BookmarkDragMetadata[`behaviorIndex`];
  /** when provided, allows for a drag and drop gripper element */
  handleRef?: (element: Element | null) => void;
  dropRef?: (element: Element | null) => void;
  stagedFilter?: Obj | null;
  abortStagedFilter?: () => void;
};

/**
 * Provides a component for rendering a behavior event selection together with filters.
 *
 * @todo(metrics) filters are not matching designs
 */
export const BehaviorEventRow = forwardRef<HTMLDivElement, BehaviorRowProps>(function (props, ref) {
  var _props$rowIcon, _props$openQueryBlock, _props$behaviorIndex, _props$stagedFilter, _ref5, _props$entry$measurem;
  const {
    print
  } = useTranslation();
  const entryElRef = useRef<HTMLDivElement>(null);
  const [isStagingFilter, setIsStagingFilter] = useState(false);
  const hasFirstTimeFilter = props.behavior.filters.some(isFirstTimeEventFilter);
  const handleAddFilter = () => setIsStagingFilter(true);
  const handleDelete = () => props.onDelete();
  const handleDuplicate = () => props.onDuplicate();
  const handleEventSelected = (selectedEvent: EventItem) => {
    const result = produce(props.behavior, behavior => {
      var _selectedEvent$id;
      behavior.name = selectedEvent.value;
      behavior.id = (_selectedEvent$id = selectedEvent.id) !== null && _selectedEvent$id !== void 0 ? _selectedEvent$id : null;
      behavior.type = selectedEvent.custom ? MetricType.CustomEvent : MetricType.Event;
    });
    setIsStagingFilter(false);
    props.onSelect(result);
  };
  const handleAddFirstTimeFilter = () => !hasFirstTimeFilter && handleFiltersUpdate({
    filters: [...props.behavior.filters, createFirstTimeEventFilter()],
    behavior: props.behavior,
    onSelect: props.onSelect
  });
  const handleRename = async () => {
    var _print;
    const promptResponse = await prompt({
      'initial-input-value': (_print = print(getEventBehaviorName(props.behavior as BookmarkTypes.Behavior))) !== null && _print !== void 0 ? _print : "",
      'allow-empty-input-value': true,
      'show-input': true
    }, {}, {
      title: _ref3 => {
        let {
          t
        } = _ref3;
        return t("query-builder.actions.rename");
      },
      confirmButtonLabel: _ref4 => {
        let {
          t
        } = _ref4;
        return t("common.done");
      }
    });
    if (promptResponse.didConfirm) {
      const name = promptResponse.inputValue.trim();
      props.onSelect(produce(props.behavior, behavior => {
        behavior.renamed = name || undefined;
      }));
    }
  };

  // expose the entry element to the parent component
  useImperativeHandle(ref, () => entryElRef.current!, []);
  return <motion.div ref={props.dropRef} className={clsx(styles.eventRow, {
    [styles.secondaryEventRow]: props.isSecondaryRow
  })} whileHover='hovered' variants={{
    hovered: {
      opacity: 1
    }
  }}>
      {/* row selector and actions */}
      <motion.div ref={entryElRef} className={styles.header} variants={{
      hovered: {
        paddingLeft: "4px"
      }
    }} transition={{
      type: "spring",
      visualDuration: 0.1,
      bounce: 0.3
    }}>
        {props.handleRef && <div className={styles.gripper} ref={props.handleRef}>
            <SvgIconReact icon={"gripper-xs"} />
          </div>}

        <div className={styles.eventIndex}>
          <SvgIconReact className={styles.icon} icon={(_props$rowIcon = props.rowIcon) !== null && _props$rowIcon !== void 0 ? _props$rowIcon : "behavior-event"} />
          {props.iconLabel}
        </div>

        {/* renders the actual event selection menu */}
        <div className={styles.content}>
          <QueryBlockReact type={MenuType.Events} showSessionEvent selectorIcon='' selectorType={QueryBlockSelectorType.Button} theme={ButtonTheme.QueryBuilder} label={props.label} disabled={props.sectionProps.disabled} userId={props.sectionProps.userId} workspaceId={props.sectionProps.workspaceId} projectId={props.sectionProps.projectId} prefixLabel={props.prefixLabel} suffixLabel={props.suffixLabel} contextPanelParentElement={entryElRef.current} selected={{
          label: null,
          renamed: props.behavior.renamed,
          id: props.behavior.id,
          value: props.behavior.name,
          custom: props.behavior.type === MetricType.CustomEvent
        } satisfies Event} onClose={() => {
          var _props$onClose;
          return (_props$onClose = props.onClose) === null || _props$onClose === void 0 ? void 0 : _props$onClose.call(props);
        }} onSelect={detail => handleEventSelected(detail.item)} open={(_props$openQueryBlock = props.openQueryBlock) !== null && _props$openQueryBlock !== void 0 ? _props$openQueryBlock : undefined} />
        </div>

        {/* overflow controls for each individual event in a behavior */}
        {!props.hideControls && <div className={styles.actions}>
            <ButtonReact theme={ButtonTheme.Tertiary} icon='filter-xs' onClick={handleAddFilter} />
            {props.onDelete && <ButtonReact theme={ButtonTheme.Tertiary} icon='x' onClick={handleDelete} />}
            <OverflowControls items={[{
          items: [/** stage filter */_objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Filter]), {}, {
            onSelect: handleAddFilter
          }), /** events can only have one first time filter */
          !hasFirstTimeFilter && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.FirstTimeFilter]), {}, {
            onSelect: handleAddFirstTimeFilter
          }), // rename event
          _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Rename]), {}, {
            onSelect: handleRename
          }), /** delete event */
          props.onDelete && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Delete]), {}, {
            onSelect: handleDelete
          }), /** duplicate event */
          props.onDuplicate && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Duplicate]), {}, {
            onSelect: handleDuplicate
          }), /** passes in any extract overflow action */
          ...(props.additionalOverflowControls || [])].filter(Boolean)
        }]} />
          </div>}
      </motion.div>

      {/* renders filters for the behavior event */}
      <div className={styles.filters}>
        <RowFilters {...props} dragData={{
        // makes filters local to each behavior event
        group: "".concat(props.entry._idx, "-").concat((_props$behaviorIndex = props.behaviorIndex) !== null && _props$behaviorIndex !== void 0 ? _props$behaviorIndex : props.index, "-inline-filters"),
        metricIndex: props.metricIndex,
        behaviorIndex: props.behaviorIndex,
        parentMetricIndex: props.parentMetricIndex
      }} behavior={props.behavior} isStaging={(_props$stagedFilter = props.stagedFilter) !== null && _props$stagedFilter !== void 0 ? _props$stagedFilter : isStagingFilter}
      /** prevents behavioral or computed filters from showing up */ otherFilterOptions={props.otherFilterOptions}
      /** makes sure only properties related to the event show up in filters */ eventNames={[props.behavior.name]} enableCohortFilters={props.enableCohortFilters} disabledItems={isXauMathType(props.entry.measurement.aggregationOperator) ? Array.from(BEHAVIOR_PROPERTIES.keys()).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}) : {}} dataGroupId={(_ref5 = (_props$entry$measurem = props.entry.measurement.dataGroupId) !== null && _props$entry$measurem !== void 0 ? _props$entry$measurem : props.entry.behavior.dataGroupId) !== null && _ref5 !== void 0 ? _ref5 : props.sectionProps.dataGroupId} onAbortStage={() => {
        var _props$abortStagedFil;
        setIsStagingFilter(false);
        (_props$abortStagedFil = props.abortStagedFilter) === null || _props$abortStagedFil === void 0 || _props$abortStagedFil.call(props);
      }} onSelect={props.onSelect} />
      </div>
    </motion.div>;
});

/**
 * Provides a component for rendering a profile based behavior selection together with filters.
 *
 * @todo(metrics) filters are not matching designs
 */
export const BehaviorProfileRow = forwardRef<HTMLDivElement, BehaviorRowProps>(function (props, ref) {
  var _props$rowIcon2, _props$behaviorIndex2, _ref6, _props$entry$measurem2;
  const entryElRef = useRef<HTMLDivElement>(null);
  const [isStagingFilter, setIsStagingFilter] = useState(false);
  const handleAddFilter = () => setIsStagingFilter(true);

  // expose the entry element to the parent component
  useImperativeHandle(ref, () => entryElRef.current!, []);
  return <motion.div ref={props.dropRef} className={clsx(styles.eventRow, {
    [styles.secondaryEventRow]: props.isSecondaryRow
  })} whileHover='hovered' variants={{
    hovered: {
      opacity: 1
    }
  }}>
      {/* row selector and actions */}
      <motion.div ref={entryElRef} className={styles.header} variants={{
      hovered: {
        paddingLeft: "4px"
      }
    }} transition={{
      type: "spring",
      visualDuration: 0.1,
      bounce: 0.3
    }}>
        {props.handleRef && <div className={styles.gripper} ref={props.handleRef}>
            <SvgIconReact icon={"gripper-xs"} />
          </div>}

        <div className={styles.eventIndex}>
          <SvgIconReact className={styles.icon} icon={(_props$rowIcon2 = props.rowIcon) !== null && _props$rowIcon2 !== void 0 ? _props$rowIcon2 : "behavior-event"} />
          {props.iconLabel}
        </div>

        {/* renders the actual selection menu */}
        <div className={styles.content}>
          <BehaviorSelector {...props} />
        </div>

        {/* overflow controls for each individual sub-behavior in a behavior */}
        {!props.hideControls && <div className={styles.actions}>
            <ButtonReact theme={ButtonTheme.Tertiary} icon='filter-xs' onClick={handleAddFilter} />
          </div>}
      </motion.div>

      {/* renders filters for the sub-behavior */}
      <div className={styles.filters}>
        <RowFilters {...props} dragData={{
        // makes filters local to each sub-behavior
        group: "".concat(props.entry._idx, "-").concat((_props$behaviorIndex2 = props.behaviorIndex) !== null && _props$behaviorIndex2 !== void 0 ? _props$behaviorIndex2 : props.index, "-inline-filters"),
        metricIndex: props.index,
        parentMetricIndex: props.parentMetricIndex
      }} enableCohortFilters behavior={props.entry.behavior} isStaging={isStagingFilter} dataGroupId={(_ref6 = (_props$entry$measurem2 = props.entry.measurement.dataGroupId) !== null && _props$entry$measurem2 !== void 0 ? _props$entry$measurem2 : props.entry.behavior.dataGroupId) !== null && _ref6 !== void 0 ? _ref6 : props.sectionProps.dataGroupId} onAbortStage={() => setIsStagingFilter(false)} onSelect={props.onSelect} hideEvents />
      </div>
    </motion.div>;
});